import { MEDIA_VERB_IDS, type MediaVerbId } from '@lib/verbs/ids';
// Type-only, and erased before anything runs — so the banner below still holds: no component,
// no glyph module and no SCSS enters this file's graph, and its `npx tsx` test still parses.
import type { ReactNode } from 'react';

/**
 * The media action vocabulary — ids, item shape, order. Strings and types only.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THIS FILE MUST STAY IMPORT-LIGHT. Nothing here may reach a React component.
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * Split out of `useMediaVerbs` for the same reason `@lib/verbs/ids` was split out of
 * `verbs.tsx`: that file carries glyphs, so it imports `NAV_ICONS` from `@pages` and drags
 * React components — and their `.module.scss` — in behind it. `mediaActionBar.ts` is a pure
 * function with a `tsx`-free unit test, and importing these types from the hook made the test
 * runner try to parse SCSS.
 *
 * Import from here in pure modules and tests. Import from `./useMediaVerbs` when you want the
 * hook.
 */

/**
 * `MediaVerbId` is derived from a tuple in `@lib/verbs/ids`, so renaming a verb breaks there and
 * propagates here. The rest are media housekeeping.
 */
export type MediaMenuAction =
  | MediaVerbId
  | 'copy-link'
  | 'copy-metadata'
  | 'copy-link-at-time'
  | 'open-original'
  | 'rename'
  | 'move'
  | 'download'
  | 'retry-processing'
  | 'delete';

/**
 * Which control invoked the action. The same handlers back six surfaces, so the control is a
 * parameter rather than a constant, otherwise every click would report itself as a desktop
 * kebab click.
 *
 * `'media-kebab'` is the default because it's the original caller; every other
 * surface must pass its own, and forgetting to is invisible at runtime (the event
 * still fires, just attributed to the wrong control). If you add a seventh surface,
 * add a member here first, and to `mediaMenuAction` in `Tracking`.
 *
 * `'media-modal-rail'` is deliberately absent: the moment modal's three-glyph rail was replaced
 * by a single accented CTA, which reports as `'media-modal-cta'`; the unaccented pill beside it
 * reports as `'media-modal-secondary'`, so promoting a verb out of the kebab is measurable on its
 * own. The old value stays in `Tracking`'s union as a historical one, because it is still in the
 * dashboards.
 */
export type MediaActionControl =
  | 'media-kebab'
  | 'media-drawer'
  | 'media-card-rail'
  | 'media-modal-cta'
  | 'media-modal-secondary'
  | 'media-table-rail';

export interface MediaMenuActionItem {
  id: MediaMenuAction;
  label: string;
  disabled: boolean;
  danger?: boolean;
  /**
   * Why it's disabled, where there's something worth saying. Rails and the CTA put this in the
   * tooltip and the accessible name; menus ignore it and let the dimming speak.
   *
   * Computed by the hooks rather than at the consumer so one place answers "why is this inert".
   * An earlier version split it across three fields — `disabled`, a `requiresIndexed` flag, and
   * a separate permanent reason — with the precedence rule living in the consumer, which meant
   * every new surface that rendered reasons had to re-derive it or silently show none.
   *
   * Absent means only one thing: the action is available. Every disabled verb carries a reason,
   * including the terminal states — an earlier version withheld it for `errored` on the grounds
   * that the Retry control beside it said so, which was false on the two surfaces that render no
   * Retry at all (see `mediaVerbUnavailableReason`). `rename` and `delete` carry none because
   * they're menu-only and menus don't render reasons.
   */
  disabledReason?: string;
}

/**
 * A row the HOST contributes to the media menu, for something only the host can do.
 *
 * Deliberately NOT a `MediaMenuAction`. That union feeds the hooks, so a member added to it
 * appears on every surface that renders a media menu — grid card, table row, moment modal — and
 * the only row this shape exists for is one those surfaces cannot fulfil: "Download .srt" needs
 * the fetched transcript, which the modal holds and a grid card has never asked for. The
 * alternative was a hook fetching a transcript per card to decide whether to grey out a row.
 *
 * It is also not the `'agent-on-clip'` mistake `splitMediaActions` documents. That was a
 * duplicate of an existing verb differing only in its handler, so it belonged at the component
 * that routes the click; this is a distinct action with no counterpart in the union at all.
 *
 * Carries its own glyph because `getMediaActionIcon` is keyed on `MediaMenuAction` and this id
 * is not one. Rendered by both the desktop menu and the mobile drawer, from this one array —
 * `.claude/rules/context-menu-actions.md`.
 */
export interface MediaMenuHostItem {
  /** Unique within the host's own list; the React key. */
  id: string;
  label: string;
  icon: ReactNode;
  onSelect: () => void;
}

/**
 * The order every menu and drawer lists these in, top to bottom.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THIS EXISTS BECAUSE THE ACTIONS NOW COME FROM TWO HOOKS, NOT ONE.
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * `useMediaMenuActions` composes `useMediaVerbs` with `useMediaAdminActions`, and the two
 * cannot simply be concatenated: `rename` is an admin action and sits BETWEEN `open-original`
 * and `download`, both of which are verbs-hook actions. Concatenating would silently reorder
 * every menu in the app. So the halves each return their own items and the composer sorts by
 * this.
 *
 * `satisfies` rather than an annotation so that renaming a member of `MediaMenuAction` breaks
 * here — a missing id would otherwise sort to -1 and quietly jump to the top of every menu.
 */
export const MEDIA_ACTION_ORDER = [
  // Spread, not re-typed by hand. The verbs led this list as three literals until a review
  // pointed out that reordering `MEDIA_VERB_IDS` would silently desync the action bar's verb
  // group from every rail — `satisfies` checks membership, not order, so nothing would fail.
  ...MEDIA_VERB_IDS,
  'copy-link',
  // Directly under Copy link, ahead of the timestamped variant. Debug-only, so on almost every
  // session it isn't there at all and the two link rows are adjacent as before.
  'copy-metadata',
  'copy-link-at-time',
  'open-original',
  'rename',
  'move',
  'download',
  'retry-processing',
  'delete',
] as const satisfies readonly MediaMenuAction[];

/** Sort comparator for a merged action list. See `MEDIA_ACTION_ORDER`. */
export const byMediaActionOrder = (a: { id: string }, b: { id: string }) =>
  (MEDIA_ACTION_ORDER as readonly string[]).indexOf(a.id) -
  (MEDIA_ACTION_ORDER as readonly string[]).indexOf(b.id);
